import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Alert, Button, Card, Descriptions, Form, Input, Select, Space, Spin, Switch, Tag, Typography, message,
} from 'antd';
import {
  CopyOutlined, DeleteOutlined, EditOutlined, SwapOutlined, WhatsAppOutlined,
} from '@ant-design/icons';

import {
  getCompanyWhatsApp,
  createCompanyWhatsAppSender,
  updateCompanyWhatsAppSettings,
} from './api';
import {
  AbtBotOption, Sender, SenderEvent, apiError, copyToClipboard, formatDate, formatSender, loadAbtBotOptions,
  statusColor, statusLabel, unwrap,
} from './whatsapp/shared';
import SenderEditModal from './whatsapp/SenderEditModal';
import SenderMoveModal from './whatsapp/SenderMoveModal';
import SenderRemoveModal from './whatsapp/SenderRemoveModal';
import SenderUsersTable from './whatsapp/SenderUsersTable';
import SenderHistory from './whatsapp/SenderHistory';
import SenderTemplates from './whatsapp/SenderTemplates';

const { Text, Paragraph } = Typography;

const DEFAULT_FORMAT = '{user} ({company}): ';

interface Settings {
  attribution: boolean;
  format: string;
}

type ModalKind = 'edit' | 'move' | 'remove' | null;

/** `https://wa.me/<digits>?text=<keyword>` — the link candidates tap to start the bot. */
export const buildEntryPoint = (sender: string, keyword: string): string => {
  const digits = String(sender || '').replace(/[^0-9]/g, '');
  const text = (keyword || '').trim();
  return `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
};

/** "Sarah (Acme Recruiting): Hi Marcus…" for the attribution example. */
export const attributionExample = (format: string, companyName?: string): string => (
  `${(format || DEFAULT_FORMAT).replace('{user}', 'Sarah').replace('{company}', companyName || 'Acme Recruiting')}Hi Marcus…`
);

interface Props {
  companyId: string;
  companyName?: string;
}

/**
 * WhatsApp card on /companies (ADR-037), GoHire admins only.
 *
 * Registers the company's WhatsApp Business sender after the customer has
 * finished Infobip's embedded signup, shows the wa.me entry point, and
 * toggles the recruiter-name attribution prefix. Once connected, the number
 * can be edited, moved to another company or removed, users can be put on /
 * taken off it, its Meta templates synced from Infobip and mapped, and every
 * action shows in the history. Customers do not see this.
 */
const WhatsAppPanel: React.FC<Props> = ({ companyId, companyName }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [senders, setSenders] = useState<Sender[]>([]);
  const [retired, setRetired] = useState<Sender | null>(null);
  const [events, setEvents] = useState<SenderEvent[]>([]);
  const [settings, setSettings] = useState<Settings>({ attribution: true, format: DEFAULT_FORMAT });
  const [savingAttribution, setSavingAttribution] = useState(false);

  const [abtBots, setAbtBots] = useState<AbtBotOption[]>([]);
  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [form] = Form.useForm();

  const [keyword, setKeyword] = useState<string>('Jobs');
  const [modal, setModal] = useState<ModalKind>(null);
  /** Bumped after a full reload so the users table refetches. */
  const [usersKey, setUsersKey] = useState(0);

  /** `silent` refreshes without swapping the card for a spinner (after a user toggle). */
  const load = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!companyId) return;
    if (!silent) setLoading(true);
    setLoadError(null);
    try {
      const data = unwrap(await getCompanyWhatsApp(companyId)) || {};
      setSenders(Array.isArray(data.senders) ? data.senders : []);
      setRetired(data.retired || null);
      setEvents(Array.isArray(data.events) ? data.events : []);
      setSettings({
        attribution: !data.settings || data.settings.attribution !== false,
        format: (data.settings && data.settings.format) || DEFAULT_FORMAT,
      });
    } catch (err) {
      setSenders([]);
      setRetired(null);
      setEvents([]);
      setLoadError(apiError(err, 'Failed to load the WhatsApp settings for this company.'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    form.resetFields();
    setRegisterError(null);
    setModal(null);
    load();
  }, [load, form]);

  // Apply-By-Text bots: the registration form, the edit modal and history labels.
  useEffect(() => {
    let cancelled = false;
    setAbtBots([]);
    loadAbtBotOptions(companyId).then((opts) => { if (!cancelled) setAbtBots(opts); });
    return () => { cancelled = true; };
  }, [companyId]);

  const sender = senders[0];

  const reloadAfterAction = async () => {
    setModal(null);
    await load();
    setUsersKey((k) => k + 1);
  };
  const entryPoint = useMemo(() => (sender ? buildEntryPoint(sender.sender, keyword) : ''), [sender, keyword]);

  const handleRegister = async (values: any) => {
    setRegistering(true);
    setRegisterError(null);
    try {
      await createCompanyWhatsAppSender(companyId, {
        sender: String(values.sender || '').replace(/[^0-9]/g, ''),
        displayName: values.displayName || undefined,
        wabaId: values.wabaId || undefined,
        abtBotId: values.abtBotId || null,
      });
      message.success('WhatsApp sender registered');
      form.resetFields();
      await load();
    } catch (err) {
      setRegisterError(apiError(err, 'Failed to register the WhatsApp sender.'));
    } finally {
      setRegistering(false);
    }
  };

  const handleAttribution = async (checked: boolean) => {
    const previous = settings;
    setSettings({ ...settings, attribution: checked });
    setSavingAttribution(true);
    try {
      const saved = unwrap(await updateCompanyWhatsAppSettings(companyId, {
        attribution: checked,
        format: settings.format,
      })) || {};
      setSettings({ attribution: saved.attribution !== false, format: saved.format || settings.format });
      message.success(checked ? 'Recruiter names will prefix WhatsApp messages' : 'Attribution prefix turned off');
    } catch (err) {
      setSettings(previous);
      message.error(apiError(err, 'Failed to update the WhatsApp settings.'));
    } finally {
      setSavingAttribution(false);
    }
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(entryPoint);
    if (ok) message.success('Entry point link copied');
    else message.error('Could not copy. Select the link and copy it manually.');
  };

  return (
    <Card
      className="gh-dash-card"
      data-testid="company-whatsapp-card"
      title={(
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <WhatsAppOutlined style={{ color: '#25D366' }} />
          WhatsApp
        </span>
      )}
      extra={(
        <span style={{ color: '#8c8c8c' }}>
          Company WhatsApp sender and message attribution
        </span>
      )}
    >
      {loadError && <Alert type="error" showIcon message={loadError} style={{ marginBottom: 16 }} />}
      {loading && <Spin />}

      {!loading && !loadError && !sender && (
        <div data-testid="company-whatsapp-register">
          {retired && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }} data-testid="company-whatsapp-retired">
              Previously connected: {formatSender(retired.sender)}
              {retired.removedAt ? ` · removed ${formatDate(retired.removedAt)}` : ''}
            </Text>
          )}
          <Paragraph type="secondary" style={{ maxWidth: 640 }}>
            No WhatsApp sender is connected for this company. Once the customer has completed Infobip&apos;s
            embedded signup for their own WhatsApp Business Account, register the resulting number here.
            Every user in the company is added to the sender so unassigned conversations reach the team.
          </Paragraph>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleRegister}
            style={{ maxWidth: 480 }}
            requiredMark={false}
          >
            <Form.Item
              name="sender"
              label="Sender number"
              extra="Digits only, with country code, e.g. 15550102231"
              rules={[
                { required: true, message: 'Enter the WhatsApp sender number' },
                { pattern: /^\+?[0-9]{8,15}$/, message: '8–15 digits, country code first' },
              ]}
            >
              <Input placeholder="15550102231" autoComplete="off" inputMode="numeric" />
            </Form.Item>
            <Form.Item name="displayName" label="Display name">
              <Input placeholder={`WhatsApp - ${companyName || 'company'}`} maxLength={120} autoComplete="off" />
            </Form.Item>
            <Form.Item name="wabaId" label="WhatsApp Business Account ID" extra="Optional, from Meta Business Manager">
              <Input maxLength={64} autoComplete="off" />
            </Form.Item>
            <Form.Item
              name="abtBotId"
              label="Apply-By-Text bot"
              extra="Optional. The flow that answers candidates who message this number."
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="No bot (inbox only)"
                options={abtBots}
                notFoundContent="No Apply-By-Text bots"
              />
            </Form.Item>
            {registerError && <Alert type="error" showIcon message={registerError} style={{ marginBottom: 16 }} />}
            <Button type="primary" htmlType="submit" loading={registering}>
              Register sender
            </Button>
          </Form>
          {events.length > 0 && (
            <div style={{ marginTop: 24, maxWidth: 640 }}>
              <SenderHistory events={events} abtBots={abtBots} />
            </div>
          )}
        </div>
      )}

      {!loading && sender && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16,
        }}
        >
          <Button icon={<EditOutlined />} onClick={() => setModal('edit')} data-testid="company-whatsapp-edit">
            Edit
          </Button>
          <Button icon={<SwapOutlined />} onClick={() => setModal('move')} data-testid="company-whatsapp-move">
            Move to another company
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => setModal('remove')}
            style={{ marginLeft: 'auto' }}
            data-testid="company-whatsapp-remove"
          >
            Remove
          </Button>
        </div>
      )}

      {!loading && sender && (
        <div style={{ display: 'grid', gap: 24, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
          <div>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Number">
                <Text strong data-testid="company-whatsapp-number">{formatSender(sender.sender)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Display name">
                <span data-testid="company-whatsapp-display-name">{sender.displayName}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColor(sender.status)} data-testid="company-whatsapp-status">
                  {statusLabel(sender.status || 'ACTIVE')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Assigned users">{sender.assignedUsers}</Descriptions.Item>
              {sender.wabaId && <Descriptions.Item label="WABA ID">{sender.wabaId}</Descriptions.Item>}
            </Descriptions>

            <div style={{ fontWeight: 500, marginBottom: 4 }}>Entry point</div>
            <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
              Share this link, or a QR code of it. Candidates open WhatsApp with the keyword pre-filled.
            </Text>
            <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
              <Input
                addonBefore="Keyword"
                value={keyword}
                maxLength={40}
                onChange={(e) => setKeyword(e.target.value)}
                style={{ maxWidth: 260 }}
              />
            </Space.Compact>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                readOnly
                value={entryPoint}
                data-testid="company-whatsapp-entry-point"
                onFocus={(e) => e.target.select()}
              />
              <Button icon={<CopyOutlined />} onClick={handleCopy}>Copy</Button>
            </Space.Compact>
          </div>

          <div data-testid="company-whatsapp-attribution">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
              <div>
                <Text strong style={{ display: 'block' }}>Prefix recruiter messages with their name</Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Candidates see who they are talking to when several recruiters share the company number.
                  Bot and template messages are never prefixed.
                </Text>
              </div>
              <Switch
                checked={settings.attribution}
                loading={savingAttribution}
                onChange={handleAttribution}
                data-testid="company-whatsapp-attribution-switch"
              />
            </div>
            <div
              style={{
                marginTop: 16,
                padding: '8px 12px',
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
              }}
            >
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Example</Text>
              <Text data-testid="company-whatsapp-attribution-example">
                {settings.attribution ? attributionExample(settings.format, companyName) : 'Hi Marcus…'}
              </Text>
            </div>
          </div>
        </div>
      )}

      {!loading && sender && (
        <div
          style={{
            display: 'grid',
            gap: 24,
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            marginTop: 24,
            paddingTop: 24,
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <SenderUsersTable
            key={`${sender.botmapId}-${usersKey}`}
            companyId={companyId}
            botmapId={sender.botmapId}
            onChanged={() => load({ silent: true })}
          />
          {events.length > 0 ? <SenderHistory events={events} abtBots={abtBots} /> : <div />}
        </div>
      )}

      {!loading && sender && (
        <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #f0f0f0' }}>
          <SenderTemplates
            key={sender.botmapId}
            companyId={companyId}
            botmapId={sender.botmapId}
            onChanged={() => load({ silent: true })}
          />
        </div>
      )}

      {sender && (
        <>
          <SenderEditModal
            open={modal === 'edit'}
            companyId={companyId}
            sender={sender}
            abtBots={abtBots}
            onClose={() => setModal(null)}
            onSaved={reloadAfterAction}
          />
          <SenderMoveModal
            open={modal === 'move'}
            companyId={companyId}
            companyName={companyName}
            sender={sender}
            onClose={() => setModal(null)}
            onMoved={reloadAfterAction}
          />
          <SenderRemoveModal
            open={modal === 'remove'}
            companyId={companyId}
            companyName={companyName}
            sender={sender}
            onClose={() => setModal(null)}
            onRemoved={reloadAfterAction}
          />
        </>
      )}
    </Card>
  );
};

export default WhatsAppPanel;
